import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AppBar,
  Box,
  Button,
  Link,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import { GEMINI_GRADIENT } from '../theme';
import { adminConsoleAuthApi } from '../api/adminConsole';
import {
  ADMIN_CONSOLE_ME_QUERY_KEY,
  probeAdminSession,
  signOutOperator,
  syncAdminSessionToQueryCache,
  terminateOperatorSession,
} from '../lib/adminSession';
import { isOidcAdminAuth } from '../lib/oidc/oidcConfig';
import { firstAllowedPath, pathAllowed } from '../lib/adminPermissions';
import { rememberedApplicationsListPath } from '../lib/applicationsListFilters';
import ThemeShifter from './ThemeShifter';

/**
 * Dedicated surface for IDV / identity verification only — no orchestrator-wide admin chrome.
 * Mirrors `EmployerVerificationShell` so operators get the same affordances on both
 * verification pillars (IDV uploads + dedup vs. employment proof).
 */
export function IdentityVerificationShell(): ReactNode {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const location = useLocation();
  const applicationsTo = rememberedApplicationsListPath();
  const [authChecked, setAuthChecked] = useState(false);
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const journeysActive = location.pathname.startsWith('/identity-verification/journeys');
  const lookupActive = location.pathname.startsWith('/identity-verification/lookup');
  const operationsActive = !journeysActive && !lookupActive;

  useEffect(() => {
    let active = true;
    const verify = async () => {
      const probe = await probeAdminSession();
      if (!active) return;
      if (!probe.ready) {
        await terminateOperatorSession(qc);
        void navigate('/login?return=%2Fidentity-verification', { replace: true });
        return;
      }
      syncAdminSessionToQueryCache(qc, probe);
      setAuthChecked(true);
    };
    void verify();
    return () => {
      active = false;
    };
  }, [navigate, qc]);

  // Same admin console `me` query Layout gates on; seeded by the probe above.
  const adminConsoleMe = useQuery({
    queryKey: ADMIN_CONSOLE_ME_QUERY_KEY,
    queryFn: () => adminConsoleAuthApi.me(),
    enabled: authChecked,
    retry: false,
    refetchOnWindowFocus: true,
  });
  const me = adminConsoleMe.data;
  const allowed = Boolean(me) && pathAllowed(location.pathname, me);

  useEffect(() => {
    if (!authChecked || !me) return;
    if (!pathAllowed(location.pathname, me)) {
      void navigate(firstAllowedPath(me), { replace: true });
    }
  }, [authChecked, me, location.pathname, navigate]);

  const signOut = () => {
    void signOutOperator(qc).then(() => {
      // OIDC mode is already navigating to auth-server end_session.
      if (!isOidcAdminAuth()) void navigate('/login?return=%2Fidentity-verification', { replace: true });
    });
  };

  if (!authChecked || !allowed) {
    return null;
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          backgroundImage: 'none',
        }}
      >
        <Toolbar sx={{ gap: 1.5, flexWrap: 'wrap', py: 1 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '8px',
              background: GEMINI_GRADIENT,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.75rem' }}>IDV</Typography>
          </Box>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <BadgeRoundedIcon sx={{ fontSize: 22, color: 'primary.main' }} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                Identity verification
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                IDV sessions · dedup · customer profiles
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ flex: 1, minWidth: 8 }} />
          <ThemeShifter size="small" />
          <Button
            component={RouterLink}
            to={applicationsTo}
            size="small"
            variant="outlined"
            startIcon={<DashboardRoundedIcon />}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Full admin
          </Button>
          <Button
            size="small"
            color="inherit"
            startIcon={<LogoutRoundedIcon />}
            onClick={signOut}
            sx={{
              whiteSpace: 'nowrap',
              border: `1px solid ${alpha(theme.palette.divider, 1)}`,
            }}
          >
            Sign out
          </Button>
        </Toolbar>
      </AppBar>

      <Box
        component="main"
        sx={{
          flex: 1,
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 2, md: 2.75 },
          maxWidth: 1400,
          mx: 'auto',
          width: '100%',
          ...(isLight
            ? {}
            : {
                pb: 3,
              }),
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Data is served directly from the <strong>identity-verification</strong> API (same Postgres as IDV). Requires
          identity-verification running and <code>ADMIN_JWT_SECRET</code> matching admin-api. Operators: Vite proxies{' '}
          <code>/api/identity-verification</code> to <code>VITE_IDENTITY_VERIFICATION_PROXY_TARGET</code> (default
          localhost:3001). Console for vendors / doctrines:{' '}
          <Link component={RouterLink} to={applicationsTo}>
            Full admin
          </Link>
          .
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
          <Button
            component={RouterLink}
            to="/identity-verification"
            size="small"
            variant={operationsActive ? 'contained' : 'outlined'}
            color={operationsActive ? 'primary' : 'inherit'}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            Operations
          </Button>
          <Button
            component={RouterLink}
            to="/identity-verification/journeys"
            size="small"
            variant={journeysActive ? 'contained' : 'outlined'}
            color={journeysActive ? 'primary' : 'inherit'}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            Record journeys
          </Button>
          <Button
            component={RouterLink}
            to="/identity-verification/lookup"
            size="small"
            variant={lookupActive ? 'contained' : 'outlined'}
            color={lookupActive ? 'primary' : 'inherit'}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            Lookup by contact
          </Button>
        </Stack>
        <Outlet />
      </Box>
    </Box>
  );
}
